// The home screen's globe (GlobeArt), as plain shapes: a hex-tiled world
// seen from space (an orthographic projection), its land picked by a fixed
// wave pattern, your territory and theirs, the strike arc between them and
// the target. No randomness: every render draws the same globe.

export const GLOBE = { size: 240, r: 92 }
const C = GLOBE.size / 2
const HEX_DEG = 5                  // a hex's radius, in degrees of arc
const VIEW = { lon: 0, lat: 22 }   // the point the globe faces
const YOURS = { lon: -38, lat: 18 }
const THEIRS = { lon: 34, lat: 40 }

const rad = (d) => (d * Math.PI) / 180

// A lon/lat point on the globe's face: { x, y, front } (front: on the near side).
function project({ lon, lat }) {
    const l = rad(lon - VIEW.lon), p = rad(lat), p0 = rad(VIEW.lat)
    const x = Math.cos(p) * Math.sin(l)
    const y = Math.cos(p0) * Math.sin(p) - Math.sin(p0) * Math.cos(p) * Math.cos(l)
    const front = Math.sin(p0) * Math.sin(p) + Math.cos(p0) * Math.cos(p) * Math.cos(l)
    return { x: C + GLOBE.r * x, y: C - GLOBE.r * y, front }
}

const isLand = ({ lon, lat }) => {
    const f = Math.sin(rad(lon) * 2.1 + 1.0) * Math.cos(rad(lat) * 2.6)
        + 0.6 * Math.sin(rad(lon) * 4.7 - rad(lat) * 3.9 + 2.0)
        + 0.35 * Math.cos(rad(lon) * 1.3 + rad(lat) * 5.5)
    return Math.abs(lat) < 72 && f > 0.25
}

// Degrees of arc between two points.
function arc(a, b) {
    const d = Math.sin(rad(a.lat)) * Math.sin(rad(b.lat))
        + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.cos(rad(a.lon - b.lon))
    return (Math.acos(Math.min(1, Math.max(-1, d))) * 180) / Math.PI
}

const pts = (list) => list.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')

// Flat-top hexes on a lon/lat lattice, the near side only: { points, kind }
// (kind: 'land', 'yours' or 'theirs'; the two territories are always land).
function hexes() {
    const out = []
    const dx = HEX_DEG * 1.5, dy = HEX_DEG * Math.sqrt(3)
    for (let i = 0; i * dx <= 360; i++) {
        const lon = -180 + i * dx
        for (let lat = -84 + (i % 2) * (dy / 2); lat <= 84; lat += dy) {
            const centre = { lon, lat }
            const kind = arc(centre, YOURS) < 13 ? 'yours' : arc(centre, THEIRS) < 11 ? 'theirs' : 'land'
            if ((kind === 'land' && !isLand(centre)) || project(centre).front < 0.08) continue
            const corners = [0, 60, 120, 180, 240, 300].map(a => project({
                lon: lon + (HEX_DEG * 0.92 * Math.cos(rad(a))) / Math.max(0.2, Math.cos(rad(lat))),
                lat: lat + HEX_DEG * 0.92 * Math.sin(rad(a)),
            }))
            out.push({ points: pts(corners), kind, front: project(centre).front })
        }
    }
    return out
}

// The graticule: every 30° of latitude and longitude, near-side runs only.
function graticule() {
    const lines = []
    const run = (sample) => {
        let cur = []
        for (const p of sample) {
            const q = project(p)
            if (q.front > 0) cur.push(q)
            else if (cur.length) { lines.push(pts(cur)); cur = [] }
        }
        if (cur.length > 1) lines.push(pts(cur))
    }
    const steps = (from, to, by) => Array.from({ length: Math.floor((to - from) / by) + 1 }, (_, k) => from + k * by)
    for (const lat of [-60, -30, 0, 30, 60]) run(steps(-180, 180, 4).map(lon => ({ lon, lat })))
    for (let lon = -180; lon < 180; lon += 30) run(steps(-90, 90, 4).map(lat => ({ lon, lat })))
    return lines
}

// The strike: a curve lifting off the globe from your territory to theirs.
function strike() {
    const a = project(YOURS), b = project(THEIRS)
    const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2
    const lift = 1.45
    const cx = C + (mx - C) * lift, cy = C + (my - C) * lift - 18
    return { from: a, to: b, path: `M${a.x.toFixed(1)},${a.y.toFixed(1)} Q${cx.toFixed(1)},${cy.toFixed(1)} ${b.x.toFixed(1)},${b.y.toFixed(1)}` }
}

export function globeShapes() {
    return { hexes: hexes(), graticule: graticule(), strike: strike() }
}
